import React, { useState, useRef, useEffect } from 'react';
import * as styles from './Timeline.module.scss';
import DashedLine from '@components/DashedLine';
import useResizeObserver from '@hooks/useResizeObserver';
import classNames from 'classnames';
import { SHOW_TIMELINE_NUMBERS } from '@constants';
import { isNumber } from 'lodash';

const TimelineContent = React.forwardRef(
  (
    {
      children,
      className,
      isTimelineStart,
      topWrapper,
      startNumber,
      finalDot = null
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={classNames(styles.content, className)}
      >
        {isTimelineStart && (
          <TimelineStart
            topWrapper={topWrapper}
            number={startNumber ?? 1}
            finalDot={finalDot}
          />
        )}
        {children}
      </div>
    );
  }
);

export default TimelineContent;

let finalDotRef = null;
let finalDotNumber = 0;

const Dot = React.forwardRef(
  (
    { color = 'teal', className, number, isMainTimeline = true, showOnMobile },
    ref
  ) => {
    const showNumber = SHOW_TIMELINE_NUMBERS && isNumber(number);
    const dotRef = useRef();
    const currentRef = ref ?? dotRef;

    // Store reference dot furthest down the page.
    // Used to calculate height of timeline
    useEffect(() => {
      if (isMainTimeline && isNumber(number) && number >= finalDotNumber) {
        finalDotNumber = number;
        finalDotRef = currentRef;
      }
    }, [isMainTimeline, number, currentRef]);

    return (
      <div
        ref={currentRef}
        aria-hidden="true"
        data-num={number}
        className={classNames('dot', styles.dot, className, {
          [styles.gray]: color === 'gray',
          [styles.number]: showNumber,
          [styles.showOnMobile]: showOnMobile
        })}
      >
        {showNumber ? <span>{number}</span> : null}
      </div>
    );
  }
);

function TimelineStart({ number, topWrapper, finalDot: _finalDot }) {
  const [height, setHeight] = useState(null);
  const finalDot = _finalDot !== undefined ? _finalDot : finalDotRef;
  const line = React.createRef();
  const { height: wrapperHeight } = useResizeObserver(topWrapper, {
    checkWidth: false
  });

  useEffect(() => {
    let lineHeight =
      !wrapperHeight || wrapperHeight <= 0 ? null : wrapperHeight;

    if (lineHeight) {
      lineHeight += finalDot?.current?.offsetTop ?? 82;
      lineHeight -= line.current?.parentElement?.offsetTop ?? 0;
    }

    setHeight(lineHeight);
  }, [wrapperHeight, line, finalDot]);

  return (
    <>
      <Dot
        className={styles.start}
        color="gray"
        number={number}
      />
      <DashedLine
        ref={line}
        className={styles.line}
        style={{ height }}
      />
    </>
  );
}

export { TimelineContent, Dot };
